import React from 'react'
import { Box, Stack, Typography } from '@mui/material'

import { INTERAX_COLORS } from '../../../constants/colors'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import useAppState from '../../../hooks/useAppState'
import { useOrgTenant } from '../../organization/OrgContext'
import { useOrganizationDetail } from '../../../apiLayer/queryHooks/queries'
import { getVisibleOrgMemberships } from '../../organization/getVisibleOrgMemberships'
import { isSoleMembershipPlatformDefault } from '../../organization/shouldHideOrgChrome'
import { OrgRoleChip } from '../../organization/components/OrgRoleChip'

/**
 * Profile card showing the user's active organization + role, plus a list of
 * all visible memberships when they belong to more than one.
 *
 * Hide rules (mirroring the OrganizationSwitcher's logic):
 * - Non-super-user whose only membership is the platform default org → hide
 *   entirely (they're effectively unaffiliated; showing "Intraction" as their
 *   org would be misleading).
 * - User with 0 memberships → hide.
 * - Super-users always see their memberships (even just the default).
 *
 * The featured "active org" prefers `activeMembership` when it's in the
 * visible list, falling back to the first visible membership if the active
 * one happens to be the hidden default.
 */
export const ProfileOrganizationSection: React.FC = () => {
  const { t } = useTranslation()
  const { state } = useAppState()
  const { activeMembership } = useOrgTenant()

  const user = state.user
  const visible = user ? getVisibleOrgMemberships(user) : []
  const isOnlyInDefault = isSoleMembershipPlatformDefault(user)

  const displayedMembership = visible.find((m) => m.organizationId === activeMembership?.organizationId) ?? visible[0]

  const { data: displayedOrgRes } = useOrganizationDetail(displayedMembership?.organizationId)
  const displayedOrgName = displayedOrgRes?.data?.name ?? displayedMembership?.organizationId ?? '—'

  if (!user || isOnlyInDefault || !displayedMembership) return null

  const showMembershipsList = visible.length >= 2

  return (
    <Box
      sx={{
        padding: '24px',
        backgroundColor: INTERAX_COLORS.heritageBlue40,
        borderRadius: '12px',
        marginBottom: '24px'
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontSize: '20px',
          fontWeight: 600,
          color: INTERAX_COLORS.white,
          mb: 2
        }}
      >
        {t('facilitator.profile.organization')}
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row', md: 'row' }}
        spacing={{ xs: 1.5, sm: 2, md: 2 }}
        alignItems={{ xs: 'flex-start', sm: 'center', md: 'center' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: INTERAX_COLORS.white,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {displayedOrgName}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: INTERAX_COLORS.thunderGrey }}>
            {t('facilitator.profile.activeOrganization')}
          </Typography>
        </Box>
        <OrgRoleChip role={displayedMembership.role} />
      </Stack>

      {showMembershipsList && (
        <Box
          sx={{
            mt: 3,
            pt: 3,
            borderTop: `1px solid ${INTERAX_COLORS.white10}`
          }}
        >
          <Typography sx={{ fontSize: '13px', color: INTERAX_COLORS.thunderGrey, mb: 1.5 }}>
            {t('facilitator.profile.allMemberships')}
          </Typography>
          <Stack spacing={1}>
            {visible.map((m) => {
              const isShown = m.organizationId === displayedMembership.organizationId
              return (
                <Stack
                  key={m.organizationId}
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    sx={{
                      color: INTERAX_COLORS.white,
                      fontSize: '14px',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: isShown ? 600 : 400
                    }}
                  >
                    {isShown ? displayedOrgName : m.organizationId}
                  </Typography>
                  <OrgRoleChip role={m.role} size="small" />
                </Stack>
              )
            })}
          </Stack>
          <Typography sx={{ fontSize: '12px', color: INTERAX_COLORS.thunderGrey, mt: 1.5 }}>
            {t('facilitator.profile.switchHint')}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
